/**
 * 路由配置
 */
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useNavigate,
  Outlet,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, useLayoutEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentUser } from '@/hooks/api';
import { errorTracker, resolveRouteName } from '@/lib/error-tracking';
import { analyticsTracker } from '@/lib/analytics';
import { useGlobalRealtime } from '@/hooks/use-global-realtime';
import { realtimeDispatcher } from '@/lib/realtime/realtime-dispatcher';
import { handleNotificationRealtimeEvent } from '@/lib/realtime/notification-handler';
import {
  handleOrgCacheRealtimeEvent,
  handleProjectCacheRealtimeEvent,
} from '@/lib/realtime/project-cache-handler';
import { handleProjectCollaborationRealtimeEvent } from '@/lib/realtime/project-collaboration-handler';
import { handleProjectPresenceRealtimeEvent } from '@/lib/realtime/project-presence-handler';
import { handleAnnotationRealtimeEvent } from '@/lib/realtime/annotation-handler';
import { handleRealtimeResyncEvent } from '@/lib/realtime/resync-handler';
import { subscribeOrgMembershipRedirect } from '@/lib/notification-side-effects';
import { resolveRouteContextScope } from '@/lib/route-context-scope';
import {
  getStoredActiveOrgId,
  hasStoredActiveContext,
  useOrgContextStore,
} from '@/stores/orgContextStore';
import { usersApi } from '@/services/api/users';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import { RouteErrorFallback } from '@/components/common/RouteErrorFallback';
import { OrgContextCrossTabDialog } from '@/components/common/OrgContextCrossTabDialog';
import { AccessRevocationCountdownDialog } from '@/components/common/AccessRevocationCountdownDialog';

// 懒加载页面（带 chunk 加载失败自动重试）
const HomePage = lazyWithRetry(() => import('@/pages/HomePage'));
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const AccountAppealPage = lazyWithRetry(() => import('@/pages/AccountAppealPage'));
const WorkspacePage = lazyWithRetry(() => import('@/pages/WorkspacePage'));
const TrashPage = lazyWithRetry(() => import('@/pages/TrashPage'));
const ProjectPage = lazyWithRetry(() => import('@/pages/ProjectPage'));
const CanvasPage = lazyWithRetry(() => import('@/pages/CanvasPage'));
const OrgProducerSetupPage = lazyWithRetry(() =>
  import('@/pages/OrgProducerSetupPage').then((m) => ({
    default: m.OrgProducerSetupPage,
  }))
);
const AIWorkspacePage = lazyWithRetry(() => import('@/pages/AIWorkspacePage'));
const ParseFlowPage = lazyWithRetry(() => import('@/pages/ParseFlowPage'));
const PublicProjectSharePage = lazyWithRetry(() => import('@/pages/PublicProjectSharePage'));
const MySharesPage = lazyWithRetry(() => import('@/pages/MySharesPage'));
const CollabProjectsPage = lazyWithRetry(() => import('@/pages/CollabProjectsPage'));
const MyOrgsPage = lazyWithRetry(() => import('@/pages/MyOrgsPage'));
const AIToolboxPage = lazyWithRetry(() =>
  import('@/pages/AIToolboxPage').then((m) => ({ default: m.AIToolboxPage }))
);
const RealtimeAsrDemoPage = lazyWithRetry(() => import('@/pages/RealtimeAsrDemoPage'));
const FaceVerificationResultPage = lazyWithRetry(
  () => import('@/pages/FaceVerificationResultPage')
);
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'));
const AccountCenterPage = lazyWithRetry(() =>
  import('@/pages/account/AccountCenterPage').then((m) => ({ default: m.AccountCenterPage }))
);
const OrgManagePage = lazyWithRetry(() =>
  import('@/pages/OrgManagePage').then((m) => ({ default: m.OrgManagePage }))
);
const CertificationPage = lazyWithRetry(() =>
  import('@/pages/CertificationPage').then((m) => ({ default: m.CertificationPage }))
);
const InvitePage = lazyWithRetry(() =>
  import('@/pages/InvitePage').then((m) => ({ default: m.InvitePage }))
);
const TempMemberInvitationPage = lazyWithRetry(() =>
  import('@/pages/TempMemberInvitationPage').then((m) => ({ default: m.TempMemberInvitationPage }))
);
const ExternalMemberReviewPage = lazyWithRetry(() =>
  import('@/pages/ExternalMemberReviewPage').then((m) => ({
    default: m.ExternalMemberReviewPage,
  }))
);
const NotificationsPage = lazyWithRetry(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
);

// 页面加载 Wrapper
function PageLoader({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="text-text-secondary">加载中...</div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// 组织上下文初始化（仅在认证状态下运行，页面刷新时从服务端同步状态）
// 注意：该组件在每次路由切换时会重新挂载（ProtectedRoute 在各路由中独立存在）。
// syncFromServer 的结果写入前必须检查 isSwitching，避免用过时数据覆盖正在进行的乐观更新。
function OrgContextInitializer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { restoreFromSession, setActiveOrg, setOrgs } = useOrgContextStore();

  useEffect(() => {
    return subscribeOrgMembershipRedirect((path) => {
      navigate(path, { replace: true });
    });
  }, [navigate]);

  useLayoutEffect(() => {
    const routeScope = resolveRouteContextScope(location.pathname);

    if (routeScope.type === 'personal') {
      // 路由已明确个人身份，即使 store 仍是初始值，也要同步记录到会话。
      setActiveOrg(null, 0, { broadcast: false });
      return;
    }

    if (routeScope.type === 'org') {
      const { activeContext, activeOrgId, orgs } = useOrgContextStore.getState();
      const targetOrg = orgs.find((org) => org.orgId === routeScope.orgId);
      if (targetOrg) {
        const contextType = targetOrg.orgType;
        if (activeOrgId !== routeScope.orgId || activeContext !== contextType) {
          setActiveOrg(routeScope.orgId, contextType as 1 | 2, { broadcast: false });
        }
      } else if (orgs.length > 0 && activeOrgId === routeScope.orgId) {
        setActiveOrg(null, 0, { broadcast: false });
      }
      return;
    }

    // 业务数据决定上下文的页面（如项目页）继续沿用本地会话，避免被误清回个人。
    restoreFromSession();
  }, [location.pathname, restoreFromSession, setActiveOrg]);

  useEffect(() => {
    const routeScope = resolveRouteContextScope(location.pathname);

    // 再从服务端拉取最新状态（含组织列表等动态数据）
    let cancelled = false;
    const syncFromServer = async () => {
      try {
        const data = await usersApi.getMyContexts();
        if (!cancelled) {
          // 切换上下文期间（isSwitching=true），跳过 setActiveOrg：
          // 此时 switchContext mutation 正在飞行中，过时的 getMyContexts 数据不能覆盖乐观更新，
          // 否则会导致 Header 图标先闪错误状态，onSuccess 才纠正。
          const { isSwitching } = useOrgContextStore.getState();
          if (!isSwitching) {
            if (routeScope.type === 'personal') {
              setActiveOrg(null, 0, { broadcast: false });
            } else if (routeScope.type === 'org') {
              const targetOrg = [...data.enterprises, ...data.teams].find(
                (org) => org.orgId === routeScope.orgId
              );
              if (targetOrg) {
                setActiveOrg(routeScope.orgId, targetOrg.orgType as 1 | 2, { broadcast: false });
              } else {
                const { activeOrgId } = useOrgContextStore.getState();
                if (activeOrgId === routeScope.orgId) {
                  setActiveOrg(data.activeOrgId, data.activeContext, { broadcast: false });
                }
              }
            } else if (routeScope.type === 'unknown') {
              const { activeOrgId } = useOrgContextStore.getState();
              if (
                !activeOrgId &&
                !getStoredActiveOrgId() &&
                !hasStoredActiveContext() &&
                data.activeOrgId
              ) {
                setActiveOrg(data.activeOrgId, data.activeContext, { broadcast: false });
              }
            }
          }
          // orgs 列表始终更新，WorkspacePage.useEffect 依赖它做 orgType 查找
          setOrgs(data.enterprises, data.teams);
        }
      } catch {
        // 静默失败，保持 sessionStorage 中的本地状态
      }
    };
    syncFromServer();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, setActiveOrg, setOrgs]);

  return null;
}

// 通知初始化（仅在认证状态下运行）
function NotificationInitializer() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unregisterProjectCache = realtimeDispatcher.register(
      'project_cache',
      'project-cache-handler',
      (event, context) => handleProjectCacheRealtimeEvent(queryClient, event, context)
    );
    const unregisterNotifications = realtimeDispatcher.register(
      'notifications',
      'notification-handler',
      (event, context) => handleNotificationRealtimeEvent(queryClient, event, context)
    );
    const unregisterOrgCache = realtimeDispatcher.register(
      'org_cache',
      'org-cache-handler',
      (event, context) => handleOrgCacheRealtimeEvent(queryClient, event, context)
    );
    const unregisterProjectCollaboration = realtimeDispatcher.register(
      'project_collaboration',
      'project-collaboration-handler',
      (event, context) => handleProjectCollaborationRealtimeEvent(queryClient, event, context)
    );
    const unregisterProjectPresence = realtimeDispatcher.register(
      'project_presence',
      'project-presence-handler',
      handleProjectPresenceRealtimeEvent
    );
    const unregisterAnnotations = realtimeDispatcher.register(
      'annotations',
      'annotation-handler',
      (event, context) => handleAnnotationRealtimeEvent(queryClient, event, context)
    );
    const unregisterResync = realtimeDispatcher.register(
      'resync',
      'resync-handler',
      (event, context) => handleRealtimeResyncEvent(queryClient, event, context)
    );
    return () => {
      unregisterProjectCache();
      unregisterNotifications();
      unregisterOrgCache();
      unregisterProjectCollaboration();
      unregisterProjectPresence();
      unregisterAnnotations();
      unregisterResync();
    };
  }, [queryClient]);

  useGlobalRealtime();

  return null;
}

// 路由保护组件
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, syncUser } = useAuth();
  const location = useLocation();

  // 只有在已认证时才调用 useCurrentUser
  const { data: currentUser, isLoading } = useCurrentUser({
    enabled: isAuthenticated,
  });

  // 同步用户信息到 AuthContext（不触发跨标签页同步）
  useEffect(() => {
    if (currentUser && isAuthenticated && !user) {
      syncUser(currentUser);
    }
  }, [currentUser, isAuthenticated, user, syncUser]);

  // 如果未认证，跳转登录
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // 如果正在加载用户信息
  if (isLoading && !user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-text-secondary">加载用户信息...</div>
      </div>
    );
  }

  return (
    <>
      <OrgContextInitializer />
      <NotificationInitializer />
      <OrgContextCrossTabDialog />
      <AccessRevocationCountdownDialog />
      {children}
    </>
  );
}

const router = createBrowserRouter([
  {
    // 根路由：统一捕获所有子路由的渲染错误
    errorElement: <RouteErrorFallback />,
    element: <Outlet />,
    children: [
      {
        path: ROUTES.HOME,
        element: (
          <PageLoader>
            <HomePage />
          </PageLoader>
        ),
      },
      {
        path: ROUTES.LOGIN,
        element: (
          <PageLoader>
            <LoginPage />
          </PageLoader>
        ),
      },
      {
        path: ROUTES.REGISTER,
        element: (
          <PageLoader>
            <LoginPage />
          </PageLoader>
        ),
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: (
          <PageLoader>
            <LoginPage />
          </PageLoader>
        ),
      },
      {
        path: ROUTES.ACCOUNT_APPEAL,
        element: (
          <PageLoader>
            <AccountAppealPage />
          </PageLoader>
        ),
      },
      {
        path: ROUTES.PUBLIC_PROJECT_SHARE,
        element: (
          <PageLoader>
            <PublicProjectSharePage />
          </PageLoader>
        ),
      },
      {
        // RFC-004：/shared = 个人上下文·我分享的
        path: ROUTES.SHARED,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <MySharesPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        // RFC-004：/shared/:orgId = 组织上下文·我分享的（URL 作为 activeOrg 唯一来源）
        path: ROUTES.SHARED_ORG,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <MySharesPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MY_ORGS,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <MyOrgsPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.AI_TOOLBOX,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <AIToolboxPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.AI_TOOLBOX_TOOL,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <AIToolboxPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.AI_TOOLBOX_ORG,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <AIToolboxPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.AI_TOOLBOX_ORG_TOOL,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <AIToolboxPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.REALTIME_ASR_DEMO,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <RealtimeAsrDemoPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ACCOUNT,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <AccountCenterPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ACCOUNT_ROOT,
        element: <Navigate to={ROUTES.ACCOUNT_PROFILE} replace />,
      },
      {
        path: ROUTES.PUBLIC_FACE_VERIFICATION_RESULT,
        element: (
          <PageLoader>
            <FaceVerificationResultPage />
          </PageLoader>
        ),
      },
      {
        path: '/projects', // Legacy redirect
        element: <Navigate to={ROUTES.WORKSPACE} replace />,
      },
      {
        path: '/org/new', // 新建团队已改为弹窗，直接重定向
        element: <Navigate to={ROUTES.WORKSPACE} replace />,
      },
      // 个人工作台（无 org 上下文）
      {
        path: ROUTES.WORKSPACE,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <WorkspacePage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.WORKSPACE_FOLDER,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <WorkspacePage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        // RFC-004：/collab 切换为独立扁平列表（不再共用 workspace 树）
        path: ROUTES.COLLAB,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <CollabProjectsPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.COLLAB_FOLDER,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <CollabProjectsPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      // Org 工作台（携带 :orgId，静态 f/ 段优先，不与个人路由冲突）
      {
        path: ROUTES.WORKSPACE_ORG,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <WorkspacePage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.WORKSPACE_ORG_FOLDER,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <WorkspacePage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        // RFC-004：组织上下文·协作项目 = 与其他团队/企业合作的项目（PCO）
        path: ROUTES.COLLAB_ORG,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <CollabProjectsPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.COLLAB_ORG_FOLDER,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <CollabProjectsPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.TRASH,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <TrashPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.TRASH_ORG,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <TrashPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROJECT_ORG_PRODUCER_SETUP,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <OrgProducerSetupPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.AI_WORKSPACE,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <AIWorkspacePage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PARSE_FLOW,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <ParseFlowPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PARSE_FLOW_LEGACY,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <ParseFlowPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROJECT_CANVAS,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <CanvasPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROJECT,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <ProjectPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      // 公开邀请落地页（无需认证可预览，接受需登录）
      {
        path: ROUTES.INVITE,
        element: (
          <PageLoader>
            <InvitePage />
          </PageLoader>
        ),
      },
      {
        path: ROUTES.TEMP_MEMBER_INVITE,
        element: (
          <PageLoader>
            <TempMemberInvitationPage />
          </PageLoader>
        ),
      },
      // 组织设置
      {
        path: ROUTES.ORG_EXTERNAL_MEMBER_REVIEWS,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <ExternalMemberReviewPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.ORG_SETTINGS,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <OrgManagePage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      // 企业认证
      {
        path: ROUTES.CERTIFICATION,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <CertificationPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      // 通知列表
      {
        path: ROUTES.NOTIFICATIONS,
        element: (
          <ProtectedRoute>
            <PageLoader>
              <NotificationsPage />
            </PageLoader>
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: (
          <PageLoader>
            <NotFoundPage />
          </PageLoader>
        ),
      },
    ],
  },
]);

// 订阅路由变化，同步路由上下文并触发 page_view 埋点
router.subscribe((state) => {
  const routeName = resolveRouteName(state.location.pathname);
  errorTracker.setRouteContext({
    pathname: state.location.pathname,
    routeName,
  });
  analyticsTracker.page();
});

export function Router() {
  return <RouterProvider router={router} />;
}
